'use client';

import { Box, TextField, ThemeProvider, createTheme } from '@mui/material';
import { type FC, useEffect, useState } from 'react';

import type { FormProps } from 'molecules/Form/config';

interface FormInterProps extends FormProps {
  value?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  validate?: (required: boolean, type: string, value: string, label: string, fieldName?: string) => string | undefined;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const Form: FC<FormInterProps> = ({
  label = '',
  fieldName = '',
  variant = 'standard',
  helperText = '',
  value = '',
  required = false,
  disabled = false,
  type = 'text',
  children,
  validate,
  onChange,
  ...props
}) => {
  const theme = createTheme({
    palette: {
      primary: {
        light: '#5095a4',
        main: '#24272A',
        dark: '#000000',
        contrastText: '#ffffff',
      },
      secondary: {
        light: '#5f5f5f',
        main: '#353535',
        dark: '#0f0f0f',
        contrastText: '#ffffff',
      },
    },
    typography: {
      fontFamily: [
        'acumin-pro-semi-condensed',
        '-apple-system',
        'system-ui',
        'SegoeUI',
        'Roboto',
        'Oxygen',
        'Ubuntu',
        'Cantarell',
        'Fira Sans',
        'Droid Sans',
        'Helvetica Neue',
        'sans-serif',
      ].join(','),
      fontSize: 25,
    },
  });

  const labelShrink = type === 'date';
  const [error, setError] = useState<string | undefined>('');
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    onChange(event);
    if (validate) {
      const errorMsg = validate(required, type, newValue, label, fieldName);
      setError(errorMsg);
    }
  };

  useEffect(() => {
    if (validate && disabled) {
      const errorMsg = validate(required, type, value, label, fieldName);
      setError(errorMsg);
    }
  }, [disabled]);

  const handleBlur = () => {
    setIsFocused(false);
    if (type === 'date' && required) {
      const date = new Date(value);
      if (date.toString() === 'Invalid Date') {
        setError(`${label} must be a \`date\` type, but the final value was: \`Invalid Date\`.`);
      }
    } else if (validate) {
      const errorMsg = validate(required, type, value, label, fieldName);
      setError(errorMsg);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ width: { xs: '95%', sm: '95%', md: '80%', lg: '80%', xl: '80%' }, minWidth: '80%' }}>
        <TextField
          sx={{
            m: 1,
            width: '100%',
            minWidth: '80%',
            '& .MuiOutlinedInput-root': {
              borderColor: error ? 'rgb(211, 47, 47)' : 'black',
            },
            '& .MuiInput-underline:after': {
              borderBottomColor: error ? 'rgb(211, 47, 47)' : 'black',
            },
            '& .MuiInput-underline.Mui-focused:after': {
              borderBottomColor: error ? 'rgb(211, 47, 47)' : 'black',
            },
            '& .MuiInputBase-root.Mui-focused': {
              borderColor: error ? 'rgb(211, 47, 47)' : 'black',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: error ? 'rgb(211, 47, 47)' : 'black',
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: error ? 'rgb(211, 47, 47)' : 'black',
            },
            '& .MuiFormHelperText-root': {
              color: error ? 'rgb(211, 47, 47)' : '#4d4d4d',
              fontSize: '1.5rem',
              marginLeft: '0px',
              marginRight: '0px',
            },
          }}
          select={type === 'select'}
          variant={variant}
          label={fieldName === 'NPI' && (isFocused || value) ? '' : label}
          name={fieldName}
          value={value}
          type={type}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={() => setIsFocused(true)}
          required={required}
          error={Boolean(error)}
          helperText={type !== 'textarea' ? error || helperText : undefined}
          multiline={type === 'textarea'}
          rows={type === 'textarea' ? 4 : undefined}
          disabled={disabled}
          SelectProps={labelShrink ? undefined : { native: true }}
          InputLabelProps={labelShrink ? { shrink: true } : undefined}
          {...props}
        >
          {children}
        </TextField>
        {type === 'textarea' && (
          <Box
            sx={{
              width: '100%', // Matches the TextField width
              marginLeft: '8px',
            }}
          >
            {error && (
              <p style={{ color: 'rgb(211, 47, 47)', fontSize: '1.34rem', margin: '-3px 14px 8px', lineHeight: 1.66 }}>
                {error}
              </p>
            )}
            {helperText && <p style={{ color: '#4d4d4d', fontSize: '1.5rem', margin: 0 }}>{helperText}</p>}
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default Form;
